import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { ReadMore } from "@seek/profile-read-ui";
import { useTranslations } from "@vocab/react";
import { IconRenderer, Dialog, Stack, Text, IconPersonVerified, IconDate, IconHash, TextLink, IconArrow, Box, Heading, Inline, Rating, IconCompany, IconPeople, IconGift, Actions, ButtonLink } from "braid-design-system";
import { createContext, useRef, useState, useContext, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { translations } from "../../../components/VerifiedHirerInfo/.vocab/index.mjs";
import { translations as translations$1 } from "../../../components/CompanyProfile/.vocab/index.mjs";
import { hoverLink, perksAndBenefits } from "./CompanyProfile.css.mjs";
const noop = () => {
};
const initialState = {
  reportJobAd: {
    ref: null,
    onReportJobAdDropdownClick: noop,
    setOnReportJobAdDropdownClick: noop
  }
};
const JobDetailsViewContext = createContext(initialState);
const useJobDetailsViewContext = () => {
  const {
    onAnalyticsEvent = noop,
    shouldOpenApplyInNewTab = false,
    featureFlags,
    displayCompanyProfile = false,
    ...result
  } = useContext(JobDetailsViewContext);
  return {
    ...result,
    onAnalyticsEvent,
    shouldOpenApplyInNewTab,
    featureFlags,
    displayCompanyProfile
  };
};
const useJobDetailsViewContextProviderValue = (props) => {
  const reportJobAdRef = useRef(null);
  const [onReportJobAdDropdownClick, setOnReportJobAdDropdownClick] = useState(() => {
  });
  return {
    ...props,
    reportJobAd: {
      ref: reportJobAdRef,
      onReportJobAdDropdownClick,
      setOnReportJobAdDropdownClick
    }
  };
};
const JobDetailsViewContextProvider = ({
  children,
  ...props
}) => {
  const value = useJobDetailsViewContextProviderValue(props);
  return /* @__PURE__ */ jsx(JobDetailsViewContext.Provider, { value, children });
};
const useQualities = () => {
  const { qualities } = useJobDetailsViewContext();
  const { matched = [], inferred = [], unmatched = [] } = qualities || {};
  return { matched, inferred, unmatched };
};
const useAnalyticsHandlers = (track) => {
  const onCompanyJobSearchClick = useCallback(
    (evt) => {
      track({
        type: "COMPANY_JOB_SEARCH_LINK_CLICK",
        href: evt.currentTarget.href
      });
    },
    [track]
  );
  const onCompanyProfileClick = useCallback(
    (evt) => {
      track({
        type: "COMPANY_PROFILE_LINK_CLICK",
        href: evt.currentTarget.href
      });
    },
    [track]
  );
  const onCompanyProfileWidgetImpression = useCallback(() => {
    track({
      type: "COMPANY_PROFILE_WIDGET_IMPRESSION"
    });
  }, [track]);
  const onCompanyProfileWidgetAccordionClick = useCallback(
    (showMore) => {
      track({
        type: "COMPANY_PROFILE_WIDGET_ACCORDION_LINK_CLICK",
        linkAction: showMore ? "expand" : "collapsed"
      });
    },
    [track]
  );
  const onCompanyProfileWidgetClick = useCallback(() => {
    track({
      type: "COMPANY_PROFILE_WIDGET_LINK_CLICK",
      linkPosition: "below"
    });
  }, [track]);
  const onCompanyProfileWidgetReviewsClick = useCallback(() => {
    track({
      type: "COMPANY_PROFILE_WIDGET_REVIEWS_LINK_CLICK",
      linkPosition: "below"
    });
  }, [track]);
  const onCompanyReviewsClick = useCallback(
    (evt) => {
      track({
        type: "COMPANY_REVIEWS_LINK_CLICK",
        href: evt.currentTarget.href
      });
    },
    [track]
  );
  const onContactMatchImpression = useCallback(
    (payload) => {
      track({
        type: "CONTACT_MATCH_IMPRESSION",
        contactTypes: payload
      });
    },
    [track]
  );
  const onContactMatchInteraction = useCallback(
    ({ eventType, contactType }) => {
      track({
        type: "CONTACT_MATCH_INTERACTION",
        contactType,
        eventType
      });
    },
    [track]
  );
  const onJobTitleClick = useCallback(
    (evt) => {
      track({
        type: "JOB_TITLE_LINK_CLICK",
        href: evt.currentTarget.href
      });
    },
    [track]
  );
  const onShareEmailClick = useCallback(() => {
    track({
      type: "SHARE_LINK_CLICK",
      channel: "email"
    });
  }, [track]);
  const onShareFacebookClick = useCallback(() => {
    track({
      type: "SHARE_LINK_CLICK",
      channel: "facebook"
    });
  }, [track]);
  const onShareLinkedInClick = useCallback(() => {
    track({
      type: "SHARE_LINK_CLICK",
      channel: "linkedin"
    });
  }, [track]);
  const onShareTwitterClick = useCallback(() => {
    track({
      type: "SHARE_LINK_CLICK",
      channel: "twitter"
    });
  }, [track]);
  const onTopApplicantBadgeClick = useCallback(() => {
    track({
      type: "TOP_APPLICANT_BADGE_CLICK"
    });
  }, [track]);
  const onTopApplicantBadgeImpression = useCallback(() => {
    track({
      type: "TOP_APPLICANT_BADGE_IMPRESSION"
    });
  }, [track]);
  const onSourcrImpression = useCallback(() => {
    track({
      type: "SOURCR_IMPRESSION"
    });
  }, [track]);
  const onSourcrClicked = useCallback(() => {
    track({
      type: "SOURCR_CLICKED"
    });
  }, [track]);
  const onSalaryBadgeClicked = useCallback(() => {
    track({
      type: "SALARY_BADGE_CLICKED"
    });
  }, [track]);
  const onSkillsMatchSignInPressed = useCallback(
    ({ currentPage, currentPageSection }) => {
      track({
        type: "SKILLS_MATCH_SIGN_IN_PRESSED",
        currentPage,
        currentPageSection
      });
    },
    [track]
  );
  const onSkillsMatchRegisterPressed = useCallback(
    ({ currentPage, currentPageSection }) => {
      track({
        type: "SKILLS_MATCH_REGISTER_PRESSED",
        currentPage,
        currentPageSection
      });
    },
    [track]
  );
  const onSkillsMatchFeedbackPressed = useCallback(
    ({ currentPage, linkAction }) => {
      track({
        type: "SKILLS_MATCH_FEEDBACK_PRESSED",
        currentPage,
        linkAction
      });
    },
    [track]
  );
  const onSkillsMatchProfileUpdated = useCallback(
    ({
      currentPage,
      currentPageSection,
      updateType,
      actionOrigin,
      skillsAdded,
      skillsStartTotal,
      skillsFromSuggestedTotal,
      skillsCompleteTotal
    }) => {
      track({
        type: "SKILLS_MATCH_PROFILE_UPDATED",
        currentPage,
        currentPageSection,
        updateType,
        actionOrigin,
        skillsAdded,
        skillsStartTotal,
        skillsFromSuggestedTotal,
        skillsCompleteTotal
      });
    },
    [track]
  );
  const onSkillsMatchAlert = useCallback(
    ({
      currentPage,
      currentPageSection,
      alertShownTone,
      alertShownType,
      alertShownMessage
    }) => {
      track({
        type: "SKILLS_MATCH_ALERT",
        currentPage,
        currentPageSection,
        alertShownTone,
        alertShownType,
        alertShownMessage
      });
    },
    [track]
  );
  const onSkillsMatchAccordionPressed = useCallback(
    ({ linkAction, linkSection }) => {
      track({
        type: "SKILLS_MATCH_ACCORDIAN_PRESSED",
        linkAction,
        linkSection
      });
    },
    [track]
  );
  const onRecommendedJobsViewed = useCallback(
    ({ jobId, numberOfRecommendedJobsDisplayed, jobStatus }) => {
      track({
        type: "RECOMMENDED_JOBS_VIEWED",
        jobId,
        numberOfRecommendedJobsDisplayed,
        currentPage: "job details",
        currentPageSection: "competitive recommended jobs",
        jobStatus
      });
    },
    [track]
  );
  return {
    onCompanyJobSearchClick,
    onCompanyProfileClick,
    onCompanyProfileWidgetImpression,
    onCompanyProfileWidgetAccordionClick,
    onCompanyProfileWidgetClick,
    onCompanyProfileWidgetReviewsClick,
    onCompanyReviewsClick,
    onContactMatchImpression,
    onContactMatchInteraction,
    onJobTitleClick,
    onShareEmailClick,
    onShareFacebookClick,
    onShareLinkedInClick,
    onShareTwitterClick,
    onTopApplicantBadgeClick,
    onTopApplicantBadgeImpression,
    onSourcrImpression,
    onSourcrClicked,
    onSalaryBadgeClicked,
    onSkillsMatchRegisterPressed,
    onSkillsMatchSignInPressed,
    onSkillsMatchFeedbackPressed,
    onSkillsMatchProfileUpdated,
    onSkillsMatchAlert,
    onSkillsMatchAccordionPressed,
    onRecommendedJobsViewed
  };
};
const IconVerifiedTrust = () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    className,
    fill: "currentColor",
    children: [
      /* @__PURE__ */ jsx("path", { d: "m20.352 4.063-8-3a1.013 1.013 0 0 0-.704 0l-8 3A1 1 0 0 0 3 5v7c0 6.543 8.203 10.72 8.553 10.895a1 1 0 0 0 .894 0C12.797 22.72 21 18.543 21 12V5a1 1 0 0 0-.648-.937Zm-8.353 16.8c-.774-.44-2.421-1.46-3.924-2.9A2.978 2.978 0 0 1 11 15.5h2a2.978 2.978 0 0 1 2.924 2.457c-1.503 1.442-3.151 2.465-3.925 2.905ZM19 12c0 1.57-.652 3-1.564 4.243A4.968 4.968 0 0 0 13 13.5h-2a4.968 4.968 0 0 0-4.439 2.747C5.651 15.004 5 13.573 5 12V5.693l7-2.625 7 2.625V12Z" }),
      /* @__PURE__ */ jsx("path", { d: "M8.563 8.5c0 1.896 1.541 3.438 3.437 3.438s3.438-1.542 3.438-3.438S13.896 5.062 12 5.062 8.562 6.604 8.562 8.5Zm5 0c0 .861-.702 1.563-1.563 1.563S10.437 9.36 10.437 8.5 11.14 6.937 12 6.937s1.563.702 1.563 1.563Z" })
    ]
  }
) }) });
const VerifiedHirerInfo = ({
  open,
  closeFn,
  trackMoreAboutThisCompanyClick
}) => {
  const { t } = useTranslations(translations);
  const {
    job: {
      company: { tags, registrationDate, verifiedBy, profileUrl, name }
    }
  } = useJobDetailsViewContext();
  const brandName = verifiedBy || "SEEK";
  const verificationInfo = t(
    `Review and verified by brand's trust & safety team`,
    { brand: brandName }
  );
  const registrationYear = registrationDate && new Date(registrationDate).getFullYear() || null;
  const registrationDateInfo = registrationYear ? t("Joined brand in year", {
    brand: brandName,
    year: registrationYear.toString()
  }) : null;
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "dlg-verified-hirer-info",
      title: t("Verifications"),
      onClose: closeFn,
      open,
      width: "small",
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsxs(Text, { size: "large", weight: "strong", children: [
          name,
          " ",
          /* @__PURE__ */ jsx(IconPersonVerified, {})
        ] }),
        /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
          verificationInfo ? /* @__PURE__ */ jsx(Text, { size: "small", icon: /* @__PURE__ */ jsx(IconVerifiedTrust, {}), children: verificationInfo }) : null,
          registrationDateInfo ? /* @__PURE__ */ jsx(Text, { size: "small", icon: /* @__PURE__ */ jsx(IconDate, {}), children: registrationDateInfo }) : null,
          tags && tags.length > 0 ? tags.map(({ key, value }, index) => /* @__PURE__ */ jsxs(Text, { size: "small", icon: /* @__PURE__ */ jsx(IconHash, {}), children: [
            key,
            " ",
            value
          ] }, `tag-${index}`)) : null
        ] }),
        profileUrl ? /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
          TextLink,
          {
            icon: /* @__PURE__ */ jsx(IconArrow, { direction: "right" }),
            iconPosition: "trailing",
            href: profileUrl,
            data: { automation: "company-profile-url-link" },
            onClick: () => trackMoreAboutThisCompanyClick("Verified Hirer Info"),
            children: t("More about this company")
          }
        ) }) }) : null
      ] })
    }
  );
};
const LOGO_MAX_HEIGHT = 50;
const CompanyProfile = (company) => {
  var _a;
  const {
    logo,
    profileUrl,
    reviewsUrl,
    name,
    onAnalyticsEvent = () => {
    },
    overview,
    perksAndBenefits: perksAndBenefits$1 = [],
    overallRating,
    shouldDisplayReviews = false,
    trackVerifiedHirerBadgeClick = () => {
    },
    trackMoreAboutThisCompanyClick = () => {
    }
  } = company;
  const industry = overview == null ? void 0 : overview.industry;
  const companySize = overview == null ? void 0 : overview.companySize;
  const companyDescription = (overview == null ? void 0 : overview.companyDescription) ?? [];
  const { t } = useTranslations(translations$1);
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      if (inView) {
        analytics.onCompanyProfileWidgetImpression();
      }
    },
    threshold: 0.5,
    triggerOnce: true
  });
  const [showVerifiedHirerInfo, setShowVerifiedHirerInfo] = useState(false);
  const { featureFlags, job } = useJobDetailsViewContext();
  const isVerifiedHirerBadgeEnabled = (featureFlags == null ? void 0 : featureFlags.enableVerifiedHirerBadge) ?? false;
  const isCompanyProfileVerified = ((_a = job == null ? void 0 : job.company) == null ? void 0 : _a.isVerified) ?? false;
  const shouldOpenLinkInNewTab = featureFlags == null ? void 0 : featureFlags.shouldOpenLinkInNewTab;
  const showProfile = company && name && profileUrl;
  const showVerifiedHirerBadge = isCompanyProfileVerified && isVerifiedHirerBadgeEnabled;
  return showProfile && /* @__PURE__ */ jsxs(Box, { ref: trackedComponent, children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsx(Heading, { level: "3", children: t("Company profile") }),
      /* @__PURE__ */ jsxs(
        Box,
        {
          borderRadius: "xlarge",
          boxShadow: "borderNeutralLight",
          data: { automation: "company-profile" },
          padding: "large",
          children: [
            /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
              /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
                Boolean(logo) && /* @__PURE__ */ jsx(Box, { display: "flex", height: "full", children: /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "img",
                    src: logo,
                    alt: `Company Logo for ${name}`,
                    style: {
                      maxHeight: LOGO_MAX_HEIGHT
                    }
                  }
                ) }),
                /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                  Boolean(name) && (showVerifiedHirerBadge ? /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
                    Box,
                    {
                      component: "button",
                      onClick: () => {
                        trackVerifiedHirerBadgeClick(
                          "Company Profile Widget"
                        );
                        setShowVerifiedHirerInfo(true);
                      },
                      cursor: "pointer",
                      className: hoverLink,
                      children: /* @__PURE__ */ jsxs(Heading, { level: "4", align: "left", children: [
                        name,
                        " ",
                        /* @__PURE__ */ jsx(IconPersonVerified, {})
                      ] })
                    }
                  ) }) : /* @__PURE__ */ jsx(Heading, { level: "4", align: "left", children: name })),
                  shouldDisplayReviews && overallRating && overallRating.rating > 0 && overallRating.reviewCount > 0 && Boolean(reviewsUrl) && /* @__PURE__ */ jsxs(
                    Inline,
                    {
                      space: "xxsmall",
                      alignY: "bottom",
                      data: { automation: "company-profile-review" },
                      children: [
                        /* @__PURE__ */ jsx(
                          Rating,
                          {
                            rating: overallRating.rating,
                            variant: "minimal",
                            data: {
                              automation: "company-profile-review-rating"
                            }
                          }
                        ),
                        reviewsUrl != null && /* @__PURE__ */ jsxs(Fragment, { children: [
                          /* @__PURE__ */ jsx(Text, { children: "·" }),
                          /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
                            TextLink,
                            {
                              href: reviewsUrl,
                              rel: "nofollow",
                              weight: "weak",
                              target: shouldOpenLinkInNewTab ? "_blank" : void 0,
                              data: {
                                automation: "company-profile-review-link"
                              },
                              onClick: analytics.onCompanyProfileWidgetReviewsClick,
                              children: t("{companyReviewsCount} reviews", {
                                companyReviewsCount: overallRating.reviewCount.toString()
                              })
                            }
                          ) })
                        ] })
                      ]
                    }
                  )
                ] })
              ] }) }) }),
              (Boolean(industry) || Boolean(companySize)) && /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
                Boolean(industry) && /* @__PURE__ */ jsx(Text, { size: "standard", icon: /* @__PURE__ */ jsx(IconCompany, {}), children: industry }),
                Boolean(companySize) && /* @__PURE__ */ jsx(Text, { size: "standard", icon: /* @__PURE__ */ jsx(IconPeople, {}), children: companySize })
              ] }) }),
              companyDescription && companyDescription.length > 0 && /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsx(
                ReadMore,
                {
                  moreLabel: t("Show more"),
                  lessLabel: t("Show less"),
                  linesToPreview: 3,
                  onShowMore: analytics.onCompanyProfileWidgetAccordionClick,
                  children: companyDescription.map((section, index) => /* @__PURE__ */ jsx(
                    Box,
                    {
                      component: "p",
                      marginTop: index > 0 ? "small" : "none",
                      children: section
                    },
                    `companyDescription-${index}`
                  ))
                }
              ) })
            ] }),
            perksAndBenefits$1 && perksAndBenefits$1.length > 0 && /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              /* @__PURE__ */ jsx(Text, { weight: "strong", icon: /* @__PURE__ */ jsx(IconGift, {}), children: t("Perks and benefits") }),
              /* @__PURE__ */ jsx(Inline, { space: "medium", collapseBelow: "tablet", children: perksAndBenefits$1.map((perk, index) => /* @__PURE__ */ jsx(Text, { size: "standard", children: /* @__PURE__ */ jsx(Box, { className: perksAndBenefits, children: perk }) }, `${perk}-${index}`)) })
            ] }) }) }),
            Boolean(profileUrl) && /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
              ButtonLink,
              {
                href: profileUrl,
                target: shouldOpenLinkInNewTab ? "_blank" : void 0,
                onClick: () => trackMoreAboutThisCompanyClick("Company Profile Widget"),
                data: { automation: "company-profile-profile-link" },
                icon: /* @__PURE__ */ jsx(IconArrow, { direction: "right" }),
                iconPosition: "trailing",
                children: t("More about this company")
              }
            ) }) })
          ]
        }
      )
    ] }),
    " ",
    /* @__PURE__ */ jsx(
      VerifiedHirerInfo,
      {
        open: showVerifiedHirerInfo,
        closeFn: () => setShowVerifiedHirerInfo(false),
        trackMoreAboutThisCompanyClick
      }
    )
  ] });
};
export {
  CompanyProfile,
  JobDetailsViewContextProvider,
  VerifiedHirerInfo,
  useAnalyticsHandlers,
  useJobDetailsViewContext,
  useQualities
};
